import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommentsService } from '@k12/admin-services';
import { AppInsightsService } from '@k12/error_handler';
import { LoadingSpinnerService } from '@k12/loading';
import { Comment, ModalType, ToastMessageType, SvgIcon, SeverityLevel } from '@k12/models';
import { ToastMessageService } from '@k12/notifications';
import { ChatPipe } from '@k12/pipes';
import { ModalDialogComponent } from '@k12/ui-primitives';
import { finalize, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EntraAccountIdService } from '@k12/app-services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-comment',
  standalone: true,
  imports: [ChatPipe, FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatTooltipModule],
  templateUrl: './comment.component.html',
  styleUrl: './comment.component.scss'
})
export class CommentComponent implements OnDestroy {
  @Input() comment: Comment;

  editedComment: string = '';
  iconName: string = 'add';
  isEdit: boolean = false;
  isSvgIcon: boolean = false;
  loading: boolean = true;
  svgIconName: SvgIcon;

  readonly editIcon = SvgIcon.Edit;
  readonly trashCanIcon = SvgIcon.TrashCan;
  readonly floppyDiskIcon = SvgIcon.FloppyDisk;

  private promptDialogRef: MatDialogRef<ModalDialogComponent>;

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly commentsService = inject(CommentsService);
  private readonly destroy$: Subject<void> = new Subject();
  private readonly promptDialog = inject(MatDialog);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly entraAccountIdService = inject(EntraAccountIdService);
  readonly entraUserId = this.entraAccountIdService.entraUserId;

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  allowEdit(comment: Comment): boolean {
    return (comment && comment.createdBy?.toLowerCase() === this.entraUserId()?.toLowerCase()) || false;
  }

  onToggleEdit(comment: Comment): void {
    this.isEdit = !this.isEdit;
    if (this.isEdit) {
      this.editedComment = comment.body;
    }
  }

  onCancel(): void {
    this.isEdit = false;
    this.editedComment = '';
  }

  async onSaveComment(): Promise<void> {
    if (!this.comment.body?.trim().length) {
      return;
    }
    this.spinnerService.showSpinner();
    try {
      const payload: Comment = {
        id: this.comment.id,
        body: this.editedComment.trim(),
        programPartnerId: this.comment.programPartnerId,
        commentType: this.comment.commentType
      };
      const response: Comment = await firstValueFrom(this.commentsService.saveComment(payload));
      if (!response) {
        const errMsg = 'Failed to update comment.';
        this.toastMessageService.showToast(ToastMessageType.Error, errMsg);
        this.appInsightsService.log(new Error(errMsg), SeverityLevel.error);
        return;
      }
      this.commentsService.refreshComments('save', response);
      this.toastMessageService.showToast(ToastMessageType.Success, 'Comment updated successfully!');
      this.isEdit = false;
    } catch (error: unknown) {
      const apiMessage: string = (error as HttpErrorResponse)?.error?.message || 'Failed to update comment.';
      this.appInsightsService.log(new Error(apiMessage), SeverityLevel.error);
      this.toastMessageService.showToast(ToastMessageType.Error, apiMessage);
    } finally {
      this.spinnerService.hideSpinner();
    }
  }

  delete(comment: Comment | undefined): void {
    if (!comment) {
      return;
    }

    this.promptDialogRef = this.promptDialog.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        closeButtonText: 'No',
        confirmButtonText: 'Yes',
        modalType: ModalType.YesNoModal,
        title: 'Would you like to delete this comment?'
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    this.promptDialogRef.componentInstance.onConfirmModal.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.spinnerService.showSpinner();
      this.commentsService
        .archiveComment(comment.id as string)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe({
          next: () => {
            this.commentsService.refreshComments('delete', comment);
            this.toastMessageService.showToast(ToastMessageType.Success, 'Comment deleted successfully.');
            this.promptDialogRef?.close();
          },
          error: (error: unknown) => {
            const msg: string = (error as HttpErrorResponse)?.error?.message || 'Failed to delete comment.';
            this.appInsightsService.log(new Error(msg), SeverityLevel.error);
            this.toastMessageService.showToast(ToastMessageType.Error, msg);
          }
        });
    });
  }
}
